import React from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import pathToRegexp from 'path-to-regexp';
import { allEmployeesOptionValue } from '../EmployeeFilter';
import { CHANNEL } from '../BookingChannelFilter';

type LinkRouteProps = {
  fromDate: any;
  toDate: any;
  employeeFilter?: number | null;
  selectedChannel?: string;
};

type CreateProductServiceSalesNavLinkConfig = {
  route: string;
  includeChannel: boolean;
  displayName: string;
  resolveRouteEmployeeFilter?: (props: LinkRouteProps) => number | null | undefined;
  getOnClickArgs: (props: LinkRouteProps) => any[];
};

type ProductServiceSalesNavLinkProps = RouteComponentProps<{ venueId: string }> & {
  onClick: (...args: any[]) => any;
  fromDate: any;
  toDate: any;
  employeeFilter?: number;
  selectedChannel?: string;
  className?: string;
};

export type ProductServiceSalesNavLinkPublicProps = Omit<
  ProductServiceSalesNavLinkProps,
  keyof RouteComponentProps
>;

type ProductServiceSalesHashLinkProps = ProductServiceSalesNavLinkPublicProps & {
  venueId: string | number;
};

export const resolveEmployeeFilterForChannel = ({
  employeeFilter,
  selectedChannel,
}: LinkRouteProps) => {
  return selectedChannel != null && employeeFilter == null
    ? allEmployeesOptionValue
    : employeeFilter;
};

export const toReportsHashHref = (path: string) =>
  `#${path.charAt(0) === '/' ? path.slice(1) : path}`;

const buildProductServiceSalesNavPath = (
  config: Pick<
    CreateProductServiceSalesNavLinkConfig,
    'route' | 'includeChannel' | 'resolveRouteEmployeeFilter'
  >,
  venueId: string | number,
  routeProps: LinkRouteProps,
) => {
  const { route, includeChannel, resolveRouteEmployeeFilter } = config;
  const { fromDate, toDate, employeeFilter, selectedChannel } = routeProps;

  const routeEmployeeFilter = resolveRouteEmployeeFilter
    ? resolveRouteEmployeeFilter(routeProps)
    : employeeFilter;

  return pathToRegexp.compile(route)({
    venueId,
    fromDate: fromDate.formatApiDateString(),
    toDate: toDate.formatApiDateString(),
    employeeFilter: routeEmployeeFilter,
    ...(includeChannel ? { selectedChannel } : {}),
  });
};

export const createProductServiceSalesHashLink = (
  config: CreateProductServiceSalesNavLinkConfig,
) => {
  const { includeChannel, displayName, getOnClickArgs } = config;

  const ProductServiceSalesHashLinkComponent: React.FC<ProductServiceSalesHashLinkProps> = ({
    venueId,
    onClick,
    fromDate,
    toDate,
    employeeFilter = allEmployeesOptionValue,
    selectedChannel = CHANNEL.ALL,
    className,
    children,
  }) => {
    const routeProps: LinkRouteProps = {
      fromDate,
      toDate,
      employeeFilter,
      selectedChannel: includeChannel ? selectedChannel : undefined,
    };

    const handleClick = () => {
      onClick(...getOnClickArgs(routeProps));
    };

    const href = toReportsHashHref(buildProductServiceSalesNavPath(config, venueId, routeProps));

    return (
      <a className={className} href={href} onClick={handleClick}>
        {children}
      </a>
    );
  };

  ProductServiceSalesHashLinkComponent.displayName = `${displayName}Hash`;

  return ProductServiceSalesHashLinkComponent;
};

export const createProductServiceSalesNavLink = (
  config: CreateProductServiceSalesNavLinkConfig,
) => {
  const { route, includeChannel, displayName, resolveRouteEmployeeFilter, getOnClickArgs } = config;

  const ProductServiceSalesNavLinkComponent: React.FC<ProductServiceSalesNavLinkProps> = ({
    onClick,
    fromDate,
    toDate,
    employeeFilter = allEmployeesOptionValue,
    selectedChannel = CHANNEL.ALL,
    className,
    children,
    match: {
      params: { venueId },
    },
  }) => {
    const routeProps: LinkRouteProps = {
      fromDate,
      toDate,
      employeeFilter,
      selectedChannel: includeChannel ? selectedChannel : undefined,
    };

    const handleClick = () => {
      onClick(...getOnClickArgs(routeProps));
    };

    const linkPath = buildProductServiceSalesNavPath(
      { route, includeChannel, resolveRouteEmployeeFilter },
      venueId,
      routeProps,
    );

    return (
      <Link to={linkPath} className={className} onClick={handleClick}>
        {children}
      </Link>
    );
  };

  ProductServiceSalesNavLinkComponent.displayName = displayName;

  return withRouter(ProductServiceSalesNavLinkComponent);
};
